// Rendu du sol des salles iso selon le biome.
// Chaque tile garde son diamant iso, mais la palette + texture varient.

const SEEDS = {};
const mulberry32 = (a) => () => {
  a = (a + 0x6D2B79F5) | 0;
  let t = a;
  t = Math.imul(t ^ (t >>> 15), t | 1);
  t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
  return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
};

// Palette par biome — couleurs DÉRIVÉES des décors de combat (/arenas/{biome}.png)
// pour que la salle iso soit raccord avec l'arène (ex : ruines = sol pierre/sable).
const palettes = {
  forest:    { color: "#56682a", line: "rgba(0,0,0,0.4)" },
  cavern:    { color: "#454166", line: "rgba(0,0,0,0.45)" },
  ruins:     { color: "#8a8050", line: "rgba(0,0,0,0.4)" },
  glacial:   { color: "#3f93a3", line: "rgba(255,255,255,0.1)" },
  volcano:   { color: "#4a261b", line: "rgba(0,0,0,0.5)" },
  celestial: { color: "#b0a890", line: "rgba(254,240,138,0.12)" },
  astral:    { color: "#8a90c0", line: "rgba(168,85,247,0.14)" },
  abyss:     { color: "#1f1838", line: "rgba(168,85,247,0.16)" },
};

// Pour chaque (themeId, tilex, tiley) → un petit objet décrivant la texture.
const detailFor = (themeId, x, y) => {
  const key = `${themeId}-${x}-${y}`;
  if (SEEDS[key] === undefined) {
    let h = 5381;
    for (let i = 0; i < key.length; i++) h = ((h << 5) + h) ^ key.charCodeAt(i);
    SEEDS[key] = mulberry32(h >>> 0)();
  }
  return SEEDS[key];
};

// Dessine le diamant de base + texture biome.
export const drawBiomeTile = (ctx, sx, sy, TILE_W, TILE_H, themeId, tx, ty, isDark) => {
  const palette = palettes[themeId] || palettes.forest;
  // Diamant — couleur uniforme par biome, sans bordure (pas de motif damier visible)
  ctx.beginPath();
  ctx.moveTo(sx, sy - TILE_H / 2);
  ctx.lineTo(sx + TILE_W / 2, sy);
  ctx.lineTo(sx, sy + TILE_H / 2);
  ctx.lineTo(sx - TILE_W / 2, sy);
  ctx.closePath();
  ctx.fillStyle = palette.color;
  ctx.fill();

  // Détails biome
  const seed = detailFor(themeId, tx, ty);

  switch (themeId) {
    case "forest":
      // Herbe : 2-3 brins par tile, fleurs occasionnelles
      drawGrass(ctx, sx, sy, seed);
      if (seed > 0.85) drawSmallFlower(ctx, sx, sy, seed);
      break;
    case "cavern":
      // Rochers/cracks
      if (seed > 0.6) drawCrack(ctx, sx, sy, seed, "rgba(15,23,42,0.5)");
      if (seed > 0.85) drawPebble(ctx, sx, sy, seed, "#475569");
      break;
    case "ruins":
      // Pavés cassés + poussière
      drawCrack(ctx, sx, sy, seed, "rgba(0,0,0,0.4)");
      if (seed > 0.7) drawPebble(ctx, sx, sy, seed, "#78716c");
      break;
    case "glacial":
      // Glace craquelée + reflets blancs
      drawIceShine(ctx, sx, sy, seed);
      if (seed > 0.7) drawCrack(ctx, sx, sy, seed, "rgba(255,255,255,0.35)");
      break;
    case "volcano":
      // Roche carbonisée + fissures de lave
      if (seed > 0.5) drawLavaCrack(ctx, sx, sy, seed);
      if (seed > 0.85) drawPebble(ctx, sx, sy, seed, "#1c1917");
      break;
    case "celestial":
      // Or pailleté
      if (seed > 0.65) drawSparkle(ctx, sx, sy, seed, "#fde047");
      if (seed > 0.85) drawCrack(ctx, sx, sy, seed, "rgba(254,240,138,0.25)");
      break;
    case "astral":
      // Étoiles + traces violettes
      if (seed > 0.55) drawSparkle(ctx, sx, sy, seed, "#c4b5fd");
      if (seed > 0.8) drawPebble(ctx, sx, sy, seed, "#7c3aed");
      break;
    case "abyss":
      // Brume violette + ossements
      if (seed > 0.6) drawCrack(ctx, sx, sy, seed, "rgba(168,85,247,0.35)");
      if (seed > 0.88) drawSmallSkull(ctx, sx, sy);
      break;
    default:
      break;
  }
};

// === Helpers ===
function drawGrass(ctx, x, y, seed) {
  ctx.strokeStyle = "#22c55e";
  ctx.lineWidth = 1.2;
  const count = 2 + Math.floor(seed * 3);
  for (let i = 0; i < count; i++) {
    const offX = (seed * (i + 1) * 7) % 14 - 7;
    const offY = (seed * (i + 2) * 5) % 8 - 4;
    const h = 4 + seed * 4;
    ctx.beginPath();
    ctx.moveTo(x + offX, y + offY);
    ctx.lineTo(x + offX + (seed - 0.5) * 1.5, y + offY - h);
    ctx.stroke();
  }
}
function drawSmallFlower(ctx, x, y, seed) {
  const colors = ["#ec4899", "#fde047", "#a78bfa", "#fb7185"];
  const c = colors[Math.floor(seed * 4) % colors.length];
  const fx = x + (seed - 0.5) * 12;
  const fy = y + (seed - 0.5) * 6;
  ctx.fillStyle = c;
  ctx.beginPath();
  ctx.arc(fx, fy, 1.5, 0, Math.PI * 2);
  ctx.arc(fx + 2, fy, 1.2, 0, Math.PI * 2);
  ctx.arc(fx - 2, fy, 1.2, 0, Math.PI * 2);
  ctx.arc(fx, fy + 2, 1.2, 0, Math.PI * 2);
  ctx.arc(fx, fy - 2, 1.2, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "#fef3c7";
  ctx.beginPath();
  ctx.arc(fx, fy, 0.8, 0, Math.PI * 2);
  ctx.fill();
}
function drawCrack(ctx, x, y, seed, color) {
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.8;
  ctx.beginPath();
  const angle = seed * Math.PI;
  const len = 6 + seed * 8;
  ctx.moveTo(x - Math.cos(angle) * len, y - Math.sin(angle) * len * 0.5);
  ctx.lineTo(x + (seed - 0.5) * 4, y + (seed - 0.5) * 2);
  ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len * 0.5);
  ctx.stroke();
}
function drawPebble(ctx, x, y, seed, color) {
  ctx.fillStyle = color;
  const px = x + (seed - 0.5) * 14;
  const py = y + (seed - 0.5) * 6;
  ctx.beginPath();
  ctx.ellipse(px, py, 1.5 + seed * 1.5, 1 + seed, 0, 0, Math.PI * 2);
  ctx.fill();
}
function drawIceShine(ctx, x, y, seed) {
  ctx.fillStyle = `rgba(186, 230, 253, ${0.18 + seed * 0.2})`;
  ctx.beginPath();
  ctx.ellipse(x + (seed - 0.5) * 8, y - 2, 6, 2, 0, 0, Math.PI * 2);
  ctx.fill();
}
function drawLavaCrack(ctx, x, y, seed) {
  const grad = `rgba(251, 146, 60, ${0.45 + seed * 0.4})`;
  ctx.strokeStyle = grad;
  ctx.lineWidth = 1.5;
  const angle = seed * Math.PI;
  const len = 7 + seed * 6;
  ctx.beginPath();
  ctx.moveTo(x - Math.cos(angle) * len, y - Math.sin(angle) * len * 0.5);
  ctx.lineTo(x, y + (seed - 0.5) * 2);
  ctx.lineTo(x + Math.cos(angle) * len, y + Math.sin(angle) * len * 0.5);
  ctx.stroke();
  // Glow
  ctx.strokeStyle = `rgba(254, 240, 138, ${0.4 + seed * 0.3})`;
  ctx.lineWidth = 0.5;
  ctx.stroke();
}
function drawSparkle(ctx, x, y, seed, color) {
  const sx = x + (seed - 0.5) * 14;
  const sy = y + (seed - 0.5) * 6;
  ctx.fillStyle = color;
  ctx.beginPath();
  ctx.arc(sx, sy, 1.2 + seed, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = color;
  ctx.lineWidth = 0.5;
  ctx.beginPath();
  ctx.moveTo(sx - 3, sy); ctx.lineTo(sx + 3, sy);
  ctx.moveTo(sx, sy - 3); ctx.lineTo(sx, sy + 3);
  ctx.stroke();
}
function drawSmallSkull(ctx, x, y) {
  ctx.fillStyle = "rgba(231, 229, 228, 0.7)";
  ctx.beginPath();
  ctx.arc(x, y, 2.4, 0, Math.PI * 2);
  ctx.fill();
  ctx.fillStyle = "rgba(15, 23, 42, 0.9)";
  ctx.fillRect(x - 1.4, y - 0.6, 0.9, 0.9);
  ctx.fillRect(x + 0.5, y - 0.6, 0.9, 0.9);
}
